import { FormGroup, InputV2 } from '@southfields-digital/mxmz-react-ui';

import { MatchControl, SoccerMatchControl } from 'src/types/matchControl';
import { Clock } from 'src/types/matchControl/clock';
import { Contestant as ContestantType } from 'src/types/matchControl/contestant';
import { SportCategory } from 'src/types/matchControl/sport';

import Hockey from './Hockey';
import Rugby from './Rugby';
import Soccer from './Soccer';
import Tennis from './Tennis';
import Volleyball from './Volleyball';

type Props = {
  categories: SportCategory[];
  handleChangeCategory: (value: SportCategory['value'] | '') => void;
  matchControlState: Partial<MatchControl> | null | undefined;
  matchControlStateUpdated: (matchControl: MatchControl) => void;
  handleSelectImage?: (callback: (src: string) => void) => void;
};

const RundownMatchControlForm = ({
  categories,
  handleChangeCategory,
  matchControlState,
  matchControlStateUpdated,
  handleSelectImage,
}: Props) => {
  const selectedSport = matchControlState?.sport || '';

  const handleChangeContestant = (contestant: ContestantType, index: number) => {
    if (!matchControlState || !matchControlState.contestants) return;

    const updatedContestants = matchControlState.contestants.map((c, idx) =>
      idx === index ? contestant : c
    );

    matchControlStateUpdated({
      ...matchControlState,
      contestants: updatedContestants,
    } as MatchControl);
  };

  const handleChangeClock = (clock: Clock, index: number) => {
    if (!matchControlState || !('clocks' in matchControlState) || !matchControlState.clocks) return;

    const updatedClocks = [...matchControlState.clocks].map((c, idx) =>
      idx === index && clock.type === c.type ? clock : c
    );

    matchControlStateUpdated({
      ...matchControlState,
      clocks: updatedClocks,
    } as MatchControl);
  };

  const handleClickMedia = (contestant: ContestantType, index: number) => {
    if (!handleSelectImage) return;
    const callback = (imgUrl: string) => {
      handleChangeContestant({ ...contestant, imageUrl: imgUrl }, index);
    };

    handleSelectImage(callback);
  };

  const handleChangePenaltyClock = (penaltyClocks: SoccerMatchControl['penaltyClocks']) => {
    if (!matchControlState) return;

    matchControlStateUpdated({
      ...matchControlState,
      config: {
        ...matchControlState.config,
        penaltyClocks,
      },
    } as MatchControl);
  };

  return (
    <div className="flex flex-col divide-y !divide-elevated-2">
      <FormGroup className="p-4" label="Match configuration" htmlFor="matchConfiguration">
        <InputV2
          id="matchConfiguration"
          type="select"
          inputSize="lg"
          options={categories}
          onChange={(e) => handleChangeCategory(e.currentTarget.value as SportCategory['value'])}
          value={selectedSport}
          placeholder="-"
          selectablePlaceholder
          // TODO: REMOVE THIS STYLING OVERRIDE WHEN ALL INPUTS USE INPUTV2
          wrapperClassName="!min-h-[42px] !rounded-[4px] !bg-elevated-0 !border-gray-800"
        />
      </FormGroup>

      {selectedSport ? (
        <div className="flex flex-col gap-6">
          {(() => {
            switch (selectedSport) {
              case 'soccer':
                return (
                  <Soccer
                    contestants={matchControlState?.contestants || []}
                    clocks={
                      (matchControlState &&
                        'clocks' in matchControlState &&
                        matchControlState?.clocks) ||
                      []
                    }
                    penaltyClocks={
                      (matchControlState?.config &&
                        'penaltyClocks' in matchControlState.config &&
                        matchControlState.config.penaltyClocks) ||
                      undefined
                    }
                    handleChangePenaltyClock={handleChangePenaltyClock}
                    handleChangeContestant={handleChangeContestant}
                    handleChangeClock={handleChangeClock}
                    handleClickMedia={handleClickMedia}
                  />
                );
              case 'hockey':
                return (
                  <Hockey
                    handleChangeContestant={handleChangeContestant}
                    handleChangeClock={handleChangeClock}
                    contestants={matchControlState?.contestants || []}
                    handleClickMedia={handleClickMedia}
                    handleChangePenaltyClock={handleChangePenaltyClock}
                    clocks={
                      (matchControlState &&
                        'clocks' in matchControlState &&
                        matchControlState.clocks) ||
                      []
                    }
                    penaltyClocks={
                      (matchControlState?.config &&
                        'penaltyClocks' in matchControlState.config &&
                        matchControlState.config.penaltyClocks) ||
                      undefined
                    }
                  />
                );
              case 'tennis':
                return (
                  <Tennis
                    handleChangeContestant={handleChangeContestant}
                    contestants={matchControlState?.contestants || []}
                    handleClickMedia={handleClickMedia}
                  />
                );
              case 'volleyball':
                return (
                  <Volleyball
                    handleChangeContestant={handleChangeContestant}
                    contestants={matchControlState?.contestants || []}
                    handleClickMedia={handleClickMedia}
                  />
                );
              case 'rugby':
                return (
                  <Rugby
                    handleChangeContestant={handleChangeContestant}
                    handleChangeClock={handleChangeClock}
                    contestants={matchControlState?.contestants || []}
                    handleClickMedia={handleClickMedia}
                    clocks={
                      (matchControlState &&
                        'clocks' in matchControlState &&
                        matchControlState.clocks) ||
                      []
                    }
                  />
                );
              default:
                return null;
            }
          })()}
        </div>
      ) : null}
    </div>
  );
};

export default RundownMatchControlForm;
